import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { cn } from "@/lib/cn";

type Item = {
  id: string;
  tipo: string;
  pagina: string;
  citaEnPagina: string;
  urlAlegada: string | null;
  fuenteIntermedia: string | null;
  severidad: "alta" | "media" | "baja";
  estado: string;
  comoVerificar: string;
  notas: string | null;
  aportesCount: number;
};
type Resp = {
  meta: { descripcion: string };
  resumen: { total: number; porSeveridad: Record<string, number> };
  items: Item[];
};

const tipoLabel: Record<string, string> = {
  url_medio: "URL de medio sin verificar",
  cifra_spec: "Cifra hardcoded del SPEC",
  analisis_saae: "Análisis SAAE (no oficial)",
  interpretacion: "Interpretación editorial",
};

const sevChip = (s: string) =>
  ({ alta: "chip-contradiccion", media: "chip-hipotesis", baja: "chip-vacio" } as Record<string, string>)[s] ?? "chip-vacio";

export function FuentesNoVerificadasPage() {
  const [searchParams] = useSearchParams();
  const [filtroPagina, setFiltroPagina] = useState(searchParams.get("pagina") ?? "");
  const [filtroSeveridad, setFiltroSeveridad] = useState<"todas" | "alta" | "media" | "baja">("todas");
  const [aporteOpen, setAporteOpen] = useState<Item | null>(null);

  // Sync URL param → state (cuando vienes de un banner)
  useEffect(() => {
    const p = searchParams.get("pagina");
    if (p !== null) setFiltroPagina(p);
  }, [searchParams]);

  const params: Record<string, string> = {};
  if (filtroPagina) params.pagina = filtroPagina;
  if (filtroSeveridad !== "todas") params.severidad = filtroSeveridad;

  const { data, isLoading } = useQuery({
    queryKey: ["fuentes-noverificadas", filtroPagina, filtroSeveridad],
    queryFn: async () => (await api.get<Resp>("/fuentes/noverificadas", { params })).data,
  });

  return (
    <div className="container-prose py-12 md:py-16">
      <div className="flex flex-wrap items-center gap-2">
        <span className="chip-reconocido">🎯 Transparencia editorial</span>
        <span className="chip-contradiccion">🔴 Catálogo abierto</span>
      </div>

      <h1 className="mt-6 font-serif text-4xl text-ink-50 md:text-5xl">Fuentes no verificadas</h1>
      <p className="mt-3 text-xs uppercase tracking-widest text-gold-400/80">
        Auditoría editorial pública · Aportes ciudadanos bienvenidos
      </p>
      <p className="mt-4 max-w-3xl text-lg text-ink-200">
        Esta página publica el inventario de afirmaciones del sitio cuya fuente primaria{" "}
        <em>todavía no fue verificada</em>. Es una <strong className="text-ink-50">declaración voluntaria
        de las propias debilidades editoriales</strong> — el sitio publica esta información SIEMPRE,
        pero quiere que el lector sepa qué nivel de respaldo tiene cada cifra/cita.
      </p>
      <p className="mt-3 max-w-3xl text-sm text-ink-300">
        Cualquier ciudadano puede aportar la fuente que verifique o desmienta cualquier ítem. Los
        aportes pasan por moderación antes de publicarse.
      </p>

      {/* KPIs */}
      {data && (
        <section className="mt-8 grid gap-3 md:grid-cols-4">
          <Kpi label="Total en catálogo" value={String(data.resumen.total)} />
          <Kpi label="Severidad ALTA" value={String(data.resumen.porSeveridad.alta ?? 0)} highlight />
          <Kpi label="Severidad media" value={String(data.resumen.porSeveridad.media ?? 0)} />
          <Kpi label="Severidad baja" value={String(data.resumen.porSeveridad.baja ?? 0)} />
        </section>
      )}

      {/* Filtros */}
      <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-ink-700/40 pt-6">
        <span className="text-sm text-ink-300">Severidad:</span>
        {(["todas", "alta", "media", "baja"] as const).map((s) => (
          <button
            key={s}
            onClick={() => setFiltroSeveridad(s)}
            className={cn(
              "rounded-md px-3 py-1.5 text-xs font-medium uppercase tracking-wide transition",
              filtroSeveridad === s ? "bg-gold-500/15 text-gold-200" : "text-ink-300 hover:bg-ink-800 hover:text-ink-50"
            )}
          >
            {s}
          </button>
        ))}
        <input
          type="text"
          placeholder="Filtrar por página (ej. /caso-galaga)"
          value={filtroPagina}
          onChange={(e) => setFiltroPagina(e.target.value)}
          className="rounded-md border border-ink-700 bg-ink-950 px-3 py-1.5 text-sm text-ink-100 placeholder:text-ink-400"
        />
      </div>

      {/* Items */}
      <div className="mt-6 space-y-3">
        {isLoading && <div className="text-ink-300">Cargando…</div>}
        {data?.items.map((item) => (
          <article key={item.id} className="rounded-lg border border-ink-700/60 bg-ink-900/40 p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className={sevChip(item.severidad)}>severidad {item.severidad}</span>
                <span className="chip-vacio">{tipoLabel[item.tipo] ?? item.tipo}</span>
                <Link to={item.pagina} className="text-xs text-gold-300 hover:underline font-mono">
                  {item.pagina}
                </Link>
              </div>
              <span className="text-xs text-ink-400">{item.aportesCount} aportes</span>
            </div>
            <blockquote className="mt-3 border-l-2 border-sem-contradiccion/40 pl-4 italic text-ink-100">
              "{item.citaEnPagina}"
            </blockquote>
            {item.urlAlegada && (
              <div className="mt-2 text-xs text-ink-300">
                URL alegada como fuente:{" "}
                <code className="text-ink-200 break-all">{item.urlAlegada}</code>
              </div>
            )}
            {item.fuenteIntermedia && (
              <div className="mt-1 text-xs text-ink-300">
                Cargado desde: <span className="text-ink-200">{item.fuenteIntermedia}</span>
              </div>
            )}
            <details className="mt-3">
              <summary className="cursor-pointer text-xs text-gold-400/80 hover:text-gold-200">
                Cómo verificar este ítem →
              </summary>
              <p className="mt-2 text-sm text-ink-200">{item.comoVerificar}</p>
              {item.notas && (
                <p className="mt-2 text-xs text-ink-400">
                  <strong>Nota interna:</strong> {item.notas}
                </p>
              )}
            </details>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setAporteOpen(item)}
                className="rounded-md border border-gold-500/40 px-3 py-1.5 text-xs text-gold-200 hover:bg-gold-500/10"
              >
                Aportar fuente
              </button>
            </div>
          </article>
        ))}
        {data && data.items.length === 0 && (
          <div className="rounded-lg border border-ink-700/40 bg-ink-900/30 p-6 text-center text-ink-400">
            Sin ítems con esos filtros.
          </div>
        )}
      </div>

      {aporteOpen && <ModalAporte item={aporteOpen} onClose={() => setAporteOpen(null)} />}
    </div>
  );
}

function ModalAporte({ item, onClose }: { item: Item; onClose: () => void }) {
  const qc = useQueryClient();
  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [urlAportada, setUrlAportada] = useState("");
  const [comentario, setComentario] = useState("");

  const m = useMutation({
    mutationFn: async () => {
      const body: any = { fuenteId: item.id, comentario };
      if (nombre) body.nombre = nombre;
      if (email) body.email = email;
      if (urlAportada) body.urlAportada = urlAportada;
      const { data } = await api.post("/fuentes/aportes", body);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fuentes-noverificadas"] });
      onClose();
      alert("Aporte recibido. Será revisado antes de publicarse.");
    },
  });

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink-950/85 p-4 backdrop-blur" onClick={onClose}>
      <div
        className="w-full max-w-xl rounded-xl border border-gold-500/30 bg-ink-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-ink-700/60 px-6 py-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-gold-400/80">Aportar fuente</div>
            <div className="mt-1 text-xs text-ink-400">{item.pagina}</div>
          </div>
          <button onClick={onClose} className="text-ink-300 hover:text-gold-200 text-2xl leading-none">×</button>
        </div>
        <div className="px-6 py-5 space-y-4 text-sm">
          <blockquote className="border-l-2 border-sem-contradiccion/40 pl-3 italic text-ink-200 text-xs">
            "{item.citaEnPagina}"
          </blockquote>
          <div>
            <label className="block text-xs text-ink-400">Nombre (opcional)</label>
            <input value={nombre} onChange={(e) => setNombre(e.target.value)}
              className="mt-1 w-full rounded-md border border-ink-700 bg-ink-950 px-3 py-2 text-ink-100" />
          </div>
          <div>
            <label className="block text-xs text-ink-400">Email (opcional, para seguimiento)</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-md border border-ink-700 bg-ink-950 px-3 py-2 text-ink-100" />
          </div>
          <div>
            <label className="block text-xs text-ink-400">URL del documento aportado (opcional)</label>
            <input type="url" value={urlAportada} onChange={(e) => setUrlAportada(e.target.value)}
              placeholder="https://…"
              className="mt-1 w-full rounded-md border border-ink-700 bg-ink-950 px-3 py-2 text-ink-100" />
          </div>
          <div>
            <label className="block text-xs text-ink-400">Comentario / explicación (obligatorio · mín. 10 caracteres)</label>
            <textarea value={comentario} onChange={(e) => setComentario(e.target.value)} rows={4}
              className="mt-1 w-full rounded-md border border-ink-700 bg-ink-950 px-3 py-2 text-ink-100" />
          </div>
          {m.isError && (
            <div className="text-sem-contradiccion text-xs">Error al enviar. Verifica los campos.</div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="rounded-md border border-ink-700 px-3 py-1.5 text-sm text-ink-300">
              Cancelar
            </button>
            <button
              onClick={() => m.mutate()}
              disabled={m.isPending || comentario.length < 10}
              className="rounded-md bg-gold-500/20 px-3 py-1.5 text-sm text-gold-200 hover:bg-gold-500/30 disabled:opacity-50"
            >
              {m.isPending ? "Enviando…" : "Enviar aporte"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`rounded-lg border p-4 ${highlight ? "border-sem-contradiccion/40 bg-sem-contradiccion/5" : "border-ink-700/60 bg-ink-900/60"}`}>
      <div className="text-xs uppercase tracking-widest text-ink-400">{label}</div>
      <div className={`mt-1 font-mono text-xl tabular-nums ${highlight ? "text-sem-contradiccion" : "text-ink-50"}`}>{value}</div>
    </div>
  );
}
